import type { CalibrationJSONObject, CaptureFileJSONObject, CollectionSummaryJSONObject } from '@ikegps/core-api';
import { observable } from 'mobx';
import type { CollectionModel } from './collection.data';
import { CollectionModelDrawableState } from './drawable/drawable.state';
import { Geometry3D } from '../../math/geometry/geometry.3d';
import { PitchGeometryFactory } from '../../math/geometry/geometry.factory.pitch';
import { IkeFetchableRaceError } from '../../rest/fetchable';
import { IkeMap, type IMap } from '../ike.map';
import type { ModelStateBrightnessCorrection } from './brightnessCorrection/brightnessCorrection';
import type { ModelFieldStateBrightnessCorrection } from './brightnessCorrection/brightnessCorrection.state';
import type { IkeCollectionLoader } from './collection.loader';
import type { ModelStateCropParameter } from './cropParameter/cropParameter';
import type { ModelFieldStateCropParameter } from './cropParameter/cropParameter.state';
import { CollectionModelDrawable } from './drawable/drawable';
import type { ModelFieldStateAnchor } from './fields/anchor.field';
import type { ModelFieldStateFileTool } from './fields/file.tool.field';
import type { ModelFieldStateHeight } from './fields/height.field';
import type { ModelFieldStateLocation } from './fields/location.field';
import type { ModelFieldStateNumber } from './fields/number.field';
import type { ModelFieldStateVector } from './fields/vector.field';
import type { ModelFieldStateVerticalSeparation } from './fields/vertical.separation.field';
import type { ModelStateImageVisibility } from './imageVisibility/imageVisibility';

export type ModelFieldState =
	| boolean
	| string
	| string[]
	| ModelFieldStateNumber
	| ModelFieldStateLocation
	| ModelFieldStateVector
	| ModelFieldStateAnchor
	| ModelFieldStateHeight
	| ModelFieldStateBrightnessCorrection
	| ModelStateBrightnessCorrection
	| ModelStateImageVisibility
	| ModelFieldStateCropParameter
	| ModelStateCropParameter
	| ModelFieldStateVerticalSeparation
	| ModelFieldStateFileTool;

export class CollectionModelState {
	loader: IkeCollectionLoader;

	/**
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	static createGeometry3D(state: CollectionModelState, captureId: string) {
		const capture = state.getCapture(captureId);
		if (capture == null) {
			throw new IkeFetchableRaceError(`Unable to find capture ${captureId}`);
		}
		const calibration = state.getCalibration(capture.calibrationid);
		if (calibration == null) {
			throw new Error(`Capture ${captureId}: Unable to find calibration ${capture.calibrationid}`);
		}
		return PitchGeometryFactory.create({
			pitch: capture.orientation.pitch,
			distance: capture.distance,
			resolution: capture.resolution,
			calibration,
		});
	}

	constructor(loader: IkeCollectionLoader) {
		this.loader = loader;
	}

	/** Whether the state has uncommitted changes. Overridden by proxy state. */
	get isDirty(): boolean {
		return false;
	}

	/** Are changes allowed to be made */
	frozen: boolean = false;

	id: string;
	instanceId: string;
	uuid: string;
	revision: string;
	jobId: string;
	updatedAt: Date;
	updatedBy: string;
	createdAt: Date;
	collectedAt: Date | null = null;
	collectedBy: string;
	collectedWith: string;
	assignedTo: string;

	fields: IMap<ModelFieldState> = new IkeMap<ModelFieldState>();
	geometries: IMap<Geometry3D> = new IkeMap<Geometry3D>();
	drawableStates = observable.map<string, CollectionModelDrawableState>();

	getFieldValue(fieldId: string): ModelFieldState | undefined {
		return this.fields.get(fieldId);
	}

	setFieldValue<T extends ModelFieldState>(fieldId: string, state: T | null) {
		if (this.frozen) {
			throw new Error(`Field ${fieldId}: Writing frozen state.`);
		}
		this.fields.set(fieldId, state);
	}

	getDrawable(captureId: string, drawableId: string): CollectionModelDrawable {
		const drawableState = this.drawableStates.get(captureId);
		if (drawableState?.hasDrawable(drawableId)) {
			return drawableState.getDrawable(drawableId);
		}
		throw new Error(`Unable to find drawable (id=${drawableId}) for capture ${captureId}`);
	}

	/**
	 * Does a value for this field id exist
	 *
	 * @param fieldId id to check
	 * @returns True if some data exists, false otherwise
	 */
	hasFieldValue(fieldId: string): boolean {
		return this.getFieldValue(fieldId) != null;
	}

	/**
	 * Get a related capture object from its id
	 *
	 * Since not all of our data structures have access to the objectId
	 * We need to guess if this is a uuid or a objectId
	 *
	 * @param captureId Capture.objectId or Capture.uuid
	 *
	 * @deprecated Use {@link IkeCollectionLoader} directly with async methods instead.
	 */
	getCapture(captureId: string): CaptureFileJSONObject | null {
		return this.loader.Capture.getById(captureId).value;
	}

	/**
	 * Get a calibration given its objectId
	 * This is lazy loaded and will return null until the value is loaded
	 * @param calibrationId Id of the calibration to fetch
	 */
	getCalibration(calibrationId: string): CalibrationJSONObject {
		return this.loader.Calibration.getById(calibrationId).value;
	}

	/**
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	getGeometry(captureId: string): Geometry3D {
		let existing = this.geometries.get(captureId);
		if (existing == null) {
			existing = CollectionModelState.createGeometry3D(this, captureId);
			this.geometries.set(captureId, existing);
		}
		return existing;
	}

	/**
	 * Get a collection given its uuid
	 *
	 * This is lazy loaded and will return null until the value is loaded
	 *
	 * @param collectionId objectId of the collection to fetch
	 */
	getCollection(collectionId: string): CollectionSummaryJSONObject | null {
		return this.loader.CollectionSummary.getById(collectionId).value;
	}

	/**
	 * @deprecated This method has a race condition when retrieving linked collections. Use getCollectionModelAsync instead.
	 */
	getCollectionModel(collectionId: string): CollectionModel | null {
		const collectionSummary = this.loader.CollectionSummary.getById(collectionId)?.value;
		if (collectionSummary == null) {
			return null;
		}
		return this.loader.Collection.getModel(collectionSummary.revision)?.value;
	}

	async getCollectionModelAsync(collectionId: string): Promise<CollectionModel | null> {
		const collectionSummary = await this.loader.CollectionSummary.getById(collectionId).fetch();
		if (collectionSummary == null) {
			return null;
		}
		return await this.loader.Collection.getModel(collectionSummary.revision).fetch();
	}

	/**
	 * Freeze the object no changes to field data will be allowed after the state is frozen.
	 */
	freeze() {
		this.frozen = true;
	}
}
